import dayjs from 'dayjs';
import EventHandler from '../../utils/dom/event-handler';
import SelectorEngine from '../../utils/dom/selector-engine';
import DatePicker from '../../components/Datepicker';
import Select from '../../components/Select';
import Modal from '../../components/Modal';
import Request, { ApiMode } from '../../utils/Request';
import Manipulator from '../../utils/dom/manipulator';
import CourtAvailability from '../../components/CourtAvailability';
import { getSlotDuration, getFollowingSlotStartTime } from './helper';
import { timeConversion } from '../../utils/date';
import CourtBookingModal from '../../components/CourtBookingModal';
import { localStorage } from '../../utils/storage';
import { startLoadingAnimation, stopLoadingAnimation } from '../../utils/dom/loading';
import { FAVORITE_CLICK_EVENT } from '../../webComponents/FavoriteIcon';
import { HIDE_MODAL_EVENT } from '../../webComponents/Modal/BasicModal';
import { setCookie } from '../../utils/cookies';
import dataLayer from '../../utils/data-layer';

const IS_LOCAL = false;

const SELECTOR_DATEPICKER = '.js-datepicker';
const SELECTOR_MAIN_DIV = '.p-venue-details__table';

const SELECTOR_AMOUNT = '.p-venue-details__booking-price-amount';
const SELECTOR_AMOUNT_MOBILE = '.p-venue-details__booking-price-amount-mobile';

const SELECTOR_PRICE = '.p-venue-details__booking-price-desktop';
const SELECTOR_PRICE_MOBILE = '.p-venue-details__booking-price-mobile';

const SELECTOR_PRICE_LABEL_DESKTOP = ".desktop-price-label"
const SELECTOR_PRICE_LABEL_MOBILE = ".mobile-price-label"

const SELECTOR_EXCEEDED_ERROR_DESKTOP = '.p-venue-details__booking-exceeded-error-desktop';
const SELECTOR_EXCEEDED_ERROR_MOBILE = '.p-venue-details__booking-exceeded-error-mobile';

const SELECTOR_API_ERROR_DESKTOP = '.p-venue-details__booking-API-error-desktop';
const SELECTOR_API_ERROR_MOBILE = '.p-venue-details__booking-API-error-mobile';

const SELECTOR_DATE_PICKER = '.js-venue-booking-date-picker';
const SELECTOR_ERROR_BLOCK = '.lta-api-result-error';

const SELECTOR_MEMEBRSHIP_CARD_LIST = '.p-venue-details__membersip-cards-list';
const SELECTOR_LINKED_VENUES_LIST = '.p-venue-details__linked-venues-list';

const SELECTOR_TABLIST = '.lta-tablist__tabs';
const MEMEBERSHIP_IDENTIFIER = 'membership-options';
const LINKEDVENUE_IDENTIFIER = 'linked-venue';

const SELECTOR_SIGN_UP_MODAL = '#lta-web-sign-up-modal';
const SELECTOR_TOO_MANY_FAV_MODAL = "#lta-web-too-many-fav-modal";

const ONE_SECOND_IN_MILLIS = 1000;
const ONE_MINUTE_IN_MILLIS = ONE_SECOND_IN_MILLIS * 60;

class VenueDetails {
	constructor() {
		this._wrapper = SelectorEngine.findOne('.js-venue-details');
		if (!this._wrapper) {
			return;
		}
		this.fromStoreValues = false;
		this.init();
	}

	get filterData() {
		return this._filterData;
	}
	set filterData(data) {
		this._filterData = data;
		this.storeCourtDetails(data);
		if (this.slots && this.slots.length > 0) {
			this.filterSlotOption();
		}
	}

	get date() {
		return this._date;
	}
	set date(date) {
		this._date = date;
		if (new Date(date) != 'Invalid Date') {
			const [newDate] = new Date(date).toISOString().split('T');
			this.fetchSlots(newDate);
		}
	}

	get slots() {
		return this._slots;
	}
	set slots(slots) {
		this._slots = slots;
		if (slots && slots.length > 0) {
			this.filterSlotOption();
		}
	}

	get courts() {
		return this._courts;
	}
	set courts(courts = {}) {
		this._courts = courts;
		this._updateCourts();
	}

	set rowData(data) {
		this._rowData = data;
	}

	get rowData() {
		return this._rowData;
	}

	isDateNotAvailable(selectedDate) {
		if (!this.rules?.availableDates) {
			return false;
		}
		return !this.rules?.availableDates.includes(selectedDate.toLocaleDateString('en-CA'));
	}

	init() {
		this.applyButtons = SelectorEngine.find('.p-venue-details__apply-button', this._wrapper);
		this.backToVenueButton = SelectorEngine.findOne('.p-venue-details__back-venue-btn', this._wrapper);
		this.courtBookForm = SelectorEngine.findOne('.p-venue-details__court-book-form', this._wrapper);
		this.bookCourtButton = SelectorEngine.findOne('.p-venue-details__book-court', this.courtBookForm);
		this.availabliblityTable = SelectorEngine.findOne(SELECTOR_MAIN_DIV, this._wrapper);
		this.ageSoftErrorElements = SelectorEngine.find('.p-venue-details__age-soft-error', this._wrapper);
		this.ageHardErrorElements = SelectorEngine.find('.p-venue-details__age-hard-error', this._wrapper);
		this.memebrshipCardList = SelectorEngine.findOne(SELECTOR_MEMEBRSHIP_CARD_LIST, this._wrapper);
		this.linkedVenuesList = SelectorEngine.findOne(SELECTOR_LINKED_VENUES_LIST, this._wrapper);
		this.tabList = SelectorEngine.findOne(SELECTOR_TABLIST, this._wrapper);
		this.venueId = Manipulator.getDataAttribute(this._wrapper, 'venueId');
		this.source = Manipulator.getDataAttribute(this._wrapper, 'source');
		this.sourceProvider = this.source.toLowerCase();

		this.venueName = Manipulator.getDataAttribute(this._wrapper, 'venue-name');
		this.venueImageUrl = Manipulator.getDataAttribute(this._wrapper, 'venue-image-url');
		this.isUserLoggedIn = Manipulator.getDataAttribute(this._wrapper, 'is-user-loggedin');
		this.loginPageUrl = Manipulator.getDataAttribute(this._wrapper, 'login-page-url');
		this.createAccountPageUrl = Manipulator.getDataAttribute(this._wrapper, 'create-account-page-url');
		this.addVenueFavoriteApiUrl = Manipulator.getDataAttribute(this._wrapper, 'favorite-api-add');
		this.removeVenueFavoriteApiUrl = Manipulator.getDataAttribute(this._wrapper, 'favorite-api-remove');
		this.favoriteLimitReached = Manipulator.getDataAttribute(this._wrapper, 'favorite-max-count-reached');

		this.datePickerWrapper = SelectorEngine.findOne(SELECTOR_DATE_PICKER, this._wrapper);
		this.errorWrapper = SelectorEngine.findOne(SELECTOR_ERROR_BLOCK, this._wrapper);

		const startTime = SelectorEngine.findOne('.p-venue-details__time-select-start .js-lta-select', this._wrapper);
		this.startTimeSlotElement = Select.getInstance(startTime) || new Select(startTime);
		this.startTimeselectedElement = SelectorEngine.findOne('.lta-select__selected', startTime);
		EventHandler.on(
			this.startTimeSlotElement._selectNativeElement,
			'change',
			this.startTimeSlotChangeHandler.bind(this)
		);

		const endTime = SelectorEngine.findOne('.p-venue-details__time-select-end .js-lta-select', this._wrapper);
		this.endTimeSlotElement = Select.getInstance(endTime) || new Select(endTime);
		this.endTimeselectedElement = SelectorEngine.findOne('.lta-select__selected', endTime);
		EventHandler.on(this.endTimeSlotElement._selectNativeElement, 'change', this.endTimeSlotChangeHandler.bind(this));

		const selectCourt = SelectorEngine.findOne('.p-venue-details__court-select .js-lta-select', this._wrapper);
		this.courtElement = Select.getInstance(selectCourt) || new Select(selectCourt);
		EventHandler.on(this.courtElement._selectNativeElement, 'change', this.courtChangeHandler.bind(this));

		this.bookingPanelWrapper = SelectorEngine.findOne('.p-venue-details__booking-wrapper', this._wrapper);
		this.courseAvaibilityWrapper = SelectorEngine.findOne('.court-avaibility-header', this._wrapper);

		this.priceWrapper = SelectorEngine.findOne('.p-venue-details__booking-price-wrapper', this._wrapper)
		this.pricePrefixText = Manipulator.getDataAttribute(this.priceWrapper, 'text-price-prefix');
		this.groupBookingPriceText = Manipulator.getDataAttribute(this.priceWrapper, 'group-booking-price-text');
		this.priceText = Manipulator.getDataAttribute(this.priceWrapper, 'price-text');


		this.errorExceedDesktop = SelectorEngine.findOne(SELECTOR_EXCEEDED_ERROR_DESKTOP, this._wrapper);
		this.errorExceedMobile = SelectorEngine.findOne(SELECTOR_EXCEEDED_ERROR_MOBILE, this._wrapper);

		this.errorAPIDesktop = SelectorEngine.findOne(SELECTOR_API_ERROR_DESKTOP, this._wrapper);
		this.errorAPIMobile = SelectorEngine.findOne(SELECTOR_API_ERROR_MOBILE, this._wrapper);

		this.bookingAmount = SelectorEngine.findOne(SELECTOR_AMOUNT, this._wrapper);
		this.bookingAmountMobile = SelectorEngine.findOne(SELECTOR_AMOUNT_MOBILE, this._wrapper);

		this.bookingPrice = SelectorEngine.findOne(SELECTOR_PRICE, this._wrapper);
		this.bookingPriceMobile = SelectorEngine.findOne(SELECTOR_PRICE_MOBILE, this._wrapper);

		this.desktopPriceLabel = SelectorEngine.findOne(SELECTOR_PRICE_LABEL_DESKTOP, this.bookingPrice)
		this.mobilePriceLabel = SelectorEngine.findOne(SELECTOR_PRICE_LABEL_MOBILE, this.bookingPriceMobile)

		this.courtBookingModal = new CourtBookingModal(this.availabliblityTable);
		this.courtBookingModal.onCheckout((checkoutData) => {
			this.checkoutRedirect(checkoutData);
		});

		this.toggleHandlerBound = this.toggleHandler.bind(this);

		this._wrapper.addEventListener(FAVORITE_CLICK_EVENT, (event) => {
			const venue = {
				venueName: this.venueName,
				imageUrl: this.venueImageUrl,
				venueId: this.venueId
			};
			this.toggleHandler(event, venue);
		});

		setTimeout(() => {
			this.dateElement = SelectorEngine.findOne(SELECTOR_DATEPICKER, this.bookingPanelWrapper);
			this.availabilityDateElement = SelectorEngine.findOne(SELECTOR_DATEPICKER, this.courseAvaibilityWrapper);

			this.timeSlotWrapper = SelectorEngine.findOne('.p-venue-details__booking-wrapper-time', this.bookingPanelWrapper);
			this.courtWrapper = SelectorEngine.findOne('.p-venue-details__booking-wrapper-court', this.bookingPanelWrapper);
			this.timeSlotErrorElement = SelectorEngine.findOne('.lta-text-input--error', this.timeSlotWrapper);
			this.courtErrorElement = SelectorEngine.findOne('.lta-text-input--error', this.courtWrapper);

			this.datePickerComponent = DatePicker.getInstance(this.dateElement) || new DatePicker(this.dateElement, {});
			this.datePickerComponent.onChange((value) => {
				this.dateChangeHandler({ detail: { value } });
			});
			this.datePicker = this.datePickerComponent.datePicker;

			this.avaibilityDatePickerComponent =
				DatePicker.getInstance(this.availabilityDateElement) || new DatePicker(this.availabilityDateElement, {});
			this.avaibilityDatePickerComponent.onChange((value) => {
				this.dateChangeHandler({ detail: { value } });
			});
			this.avaibiltityDatePicker = this.avaibilityDatePickerComponent.datePicker;

			this.addStoredVenueDetails();

			this.datePicker.isDateDisabled = this.isDateNotAvailable.bind(this);
			this.avaibiltityDatePicker.isDateDisabled = this.isDateNotAvailable.bind(this);

			this.dateParser(this.datePickerComponent.datePicker);
			this.dateParser(this.avaibilityDatePickerComponent.datePicker);
		}, 1500);

		EventHandler.on(this.bookCourtButton, 'click', () => {
			if (this.ageSoftErrorElements.length > 0) {
				this.toggleAgeError();
			}

			const date = this.datePicker.value;

			const startTimeSlot = this.startTimeSlotElement._selectNativeElement.value;
			const endTimeSlot = this.endTimeSlotElement._selectNativeElement.value;
			const courtName = this.courtElement._selectNativeElement.value;

			let dateValid = true;
			let timeValid = true;
			let courtValid = true;

			if (!date) {
				this.dateElement.classList.add('is-invalid');
				dateValid = false;
			} else {
				this.dateElement.classList.remove('is-invalid');
				dateValid = true;
			}

			if (!startTimeSlot || !endTimeSlot) {
				this.timeSlotWrapper.classList.add('is-invalid');
				if (!startTimeSlot) {
					this.startTimeSlotElement.setError('Please select a time slot.', this.timeSlotErrorElement);
				} else {
					this.endTimeSlotElement.setError('Please select a time slot.', this.timeSlotErrorElement);
				}
				timeValid = false;
			} else {
				timeValid = true;
			}

			if (!courtName) {
				this.courtWrapper.classList.add('is-invalid');
				this.courtElement.setError('Please select a court.', this.courtErrorElement);
				courtValid = false;
			} else {
				courtValid = true;
			}

			if (dateValid && timeValid && courtValid) {
				const courtData = this.courtElement._selectNativeElement.value;
				if (this.filteredData.length > 0 && startTimeSlot !== '' && endTimeSlot !== '' && courtData !== '') {
					const requestObject = {
						bookingToken: window.bookingToken,
						courtName: this.filteredData[0].court.name
					};
					requestObject.slots = this.filteredData[0].slots;
					this.checkoutRedirect(requestObject);
				}
			}
		});

		for (const applyButton of this.applyButtons) {
			EventHandler.on(applyButton, 'click', this.openBookingPanel.bind(this));
		}

		if (!!this.backToVenueButton) {
			EventHandler.on(this.backToVenueButton, 'click', this.closeBookingPanel.bind(this));
		}
		this.hideTab(MEMEBERSHIP_IDENTIFIER, this.memebrshipCardList);
		this.hideTab(LINKEDVENUE_IDENTIFIER, this.linkedVenuesList);
		if(this.sourceProvider == 'clubspark') {
			// calling the below function only if the provider is clubspark.
			this.fetchMemberShipAndLinkedVenuesDetails();
		}

		this._tooManyFavModal = SelectorEngine.findOne(SELECTOR_TOO_MANY_FAV_MODAL, this._wrapper);
		if (this._tooManyFavModal) {
			this._tooManyFavModal.addEventListener(HIDE_MODAL_EVENT, (event) => {
				this.toggleTooManyFavouriteModal(false);
				this.removeProcessing?.();
			});
		}

		this._signUpModal = SelectorEngine.findOne(SELECTOR_SIGN_UP_MODAL, this._wrapper);
		if (this._signUpModal) {
			this._signUpModal.addEventListener(HIDE_MODAL_EVENT, (event) => {
				this.toggleSignUpModal(false);
				this.removeProcessing?.();
			});
		}
	}

	async toggleHandler(event) {
		const toggleButton = event?.target;
		if (!toggleButton || toggleButton.classList.contains('is-processing')) return;

		const { isFavorite } = event?.detail;

		toggleButton.classList.add('is-processing');
		this.removeProcessing = () => toggleButton.classList.remove('is-processing');

		const isLoggedIn = String(this.isUserLoggedIn) === 'true';
		const hasReachedLimit = String(this.favoriteLimitReached) === 'true';

		this.pushDataLayer('favourite_click', isFavorite ? 'added' : 'removed');

		if (!isLoggedIn) {
			// Case 1: Not logged in
			this.toggleSignUpModal(true);
			return;
		}

		try {
			if (!isFavorite) {
				await this.removeVenueFromFavourites(event.target);
				this.removeProcessing();
			} else if (hasReachedLimit) {
				// Case 2: Favourite Limit reached
				this.toggleTooManyFavouriteModal(true);
				return;
			} else {
				// Case 3: Add directly
				await this.addVenueToFavorites(event.target);
				this.removeProcessing();
			}
		} catch (error) {
			console.error('Error in toggleHandler:', error);
			this.removeProcessing();
		}
	}

	toggleTooManyFavouriteModal(show = false) {
		if (this._tooManyFavModal) {
			if(show) {
				this.pushDataLayer('favourite_limit_reached_prompt_shown', 'shown');
			}
			this._tooManyFavModal.setAttribute('show', show);
		}
	}

	toggleSignUpModal(show = false) {
		if (this._signUpModal) {
			this.pushDataLayer('favourite_login_prompt_shown', 'shown');
			this._signUpModal.setAttribute('show', show);
		}
	}

	async addVenueToFavorites(target) {
		if (!this.venueId || !this.venueName || !this.venueImageUrl) {
			console.error('Venue details are missing for adding to favorites');
			return;
		}

		const payload = {
			venueId: this.venueId,
			venueName: this.venueName,
			imageUrl: this.venueImageUrl
		};

		try {
			const response = await Request.put(this.addVenueFavoriteApiUrl, payload, { local: IS_LOCAL });
			if (response?.errors?.errorCode === '00') {
				target.setAttribute('is-favorite', 'true');
				this.pushDataLayer('favourite_added', 'added');
				console.log('Venue added to favourites successfully');
			} else {
				console.error('Failed to add to favourites:', response?.errors?.message || 'Unknown error');
			}
		} catch (err) {
			console.error('Failed to add to favourites:', err);
		}
	}

	async removeVenueFromFavourites(target) {
		const payload = {
			venueId: this.venueId
		};
		try {
			const response = await Request.delete(this.removeVenueFavoriteApiUrl, payload, { local: IS_LOCAL });
			if (response?.errors?.errorCode === '00') {
				target.setAttribute('is-favorite', 'false');
				this.pushDataLayer('favourite_removed', 'removed');
				console.log('Venue removed from favourites successfully');
			} else {
				console.error('Failed to remove from favourites:', response?.errors?.message || 'Unknown error');
			}
		} catch (err) {
			console.error('Failed to remove from favourites:', err);
		}
	}

	addStoredVenueDetails() {
		const venueDetailData = JSON.parse(localStorage.getItem('venueData'));
		let foundInLocalStorage = false;
		if (venueDetailData) {
			for (const venueDataID in venueDetailData) {
				if (venueDataID === this.venueId) {
					const currentTime = dayjs();
					const createdTime = dayjs(venueDetailData[venueDataID].created_at);
					const difference = currentTime.diff(createdTime);
					const getMinutesValue = Math.round(difference / ONE_MINUTE_IN_MILLIS);
					if (getMinutesValue > 20) {
						delete venueDetailData[venueDataID];
						if(Object.keys(venueDetailData).length > 0 ) {
							localStorage.setItem('venueData', JSON.stringify(venueDetailData))
						} else {
							localStorage.removeItem('venueData');
						}
					} else {
						foundInLocalStorage = true;
						this.fromStoreValues = true;
						this.filterData = {
							dateValue: venueDetailData[venueDataID].date,
							startTimeValue: venueDetailData[venueDataID].startTime ? venueDetailData[venueDataID].startTime : undefined,
							endTimeValue: venueDetailData[venueDataID].endTime ? venueDetailData[venueDataID].endTime : undefined,
							courtValue: venueDetailData[venueDataID].court,
							isFromCourt: false
						};
						this.datePickerValueSet(venueDetailData[venueDataID].date);
					}
				}
			}
		}
		if(!foundInLocalStorage) {
			this.datePickerValueSet(this.datePicker.value);
		}
	}

	toggleAgeError() {
		// Hiding Soft Error.
		for (const softErrorElement of this.ageSoftErrorElements) {
			softErrorElement.classList.add('u-hidden');
		}

		// Showing Hard Error.
		for (const hardErrorElement of this.ageHardErrorElements) {
			hardErrorElement.classList.remove('u-hidden');
		}
	}

	async checkoutRedirect(requestData) {
		this.bookCourtButton.disabled = true;

		try {
			if(!window?.isAuthenticated) {
				setCookie('sso-checkout-signup-rtn', true, 1);
			}
			const response = await Request.post(
				'/api/cart/add-court',
				requestData,
				{ local: IS_LOCAL },
				localStorage.setItem("leaveCheckoutModalUrl", window.location.href)
			).catch((error) =>
				error.error.errorCode == '99' ? false : true
			);
			if (response.success) {
				window.location.href = response.redirectUrl;
				try {
					dataLayer.push({
						event: 'court_booking',
						stepCompleted: 'Click to booking'
					});
				} catch (error) {
					console.log(error.message);
				}
			} else {
				this.showError();
			}
		} catch {
			this.showError();
		} finally {
			stopLoadingAnimation();			
        	this.bookCourtButton.disabled = false;
		}
	}

	showError() {
		this.errorAPIDesktop?.classList.remove('u-hidden');
		this.errorAPIMobile?.classList.remove('u-hidden');
	}

	slotClickHandler(event) {
		const { courtId, slotId } = Manipulator.getDataAttributes(event.currentTarget);
		this.courtBookingModal.slotsData = [...this.slots.filter((slot) => slot.court.id === courtId)];
		const courtData = this.rowData?.find((court) => court.id === courtId);
		this.courtBookingModal.openModal(
			courtData,
			courtData?.availableSlots.find((slot) => slot.slotId === slotId)
		);
	}

	dateParser(datePicker) {
		const DATE_FORMAT = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;

		datePicker.dateAdapter = {
			parse(value = '', createDate) {
				const matches = value.match(DATE_FORMAT);
				return matches ? createDate(matches[3], matches[2], matches[1]) : '';
			},
			format(date) {
				return dayjs(date).format('dddd Do MMMM');
			}
		};
	}

	dateChangeHandler({ detail: { value } }) {
		this.datePickerValueSet(value);
		if (value) {
			this.dateElement.classList.remove('is-invalid');
		}
		this.removeAllErrors();
	}

	prePopulateCourt() {
		if (
			this.filterData.startTimeValue &&
			this.filterData.endTimeValue &&
			(!this.filterData.courtValue || this.filterData.courtValue === '')
		) {
			let selectedCourt;
			if (this.filteredData.length === 1) {
				selectedCourt = this.filteredData[0];
			} else {
				const priceData = [];
				for (const filterData of this.filteredData) {
					priceData.push(Number.parseFloat(this.courtsPrice(filterData.slots)));
				}
				const isPriceSame = priceData.every((price) => price === priceData[0]);
				if (isPriceSame) {
					selectedCourt = this.filteredData[0];
				} else {
					const minPricePostion = priceData.indexOf(Math.min(...priceData));
					selectedCourt = this.filteredData[minPricePostion];
				}
			}
			if (selectedCourt) {
				this.courtElement._selectNativeElement.value = selectedCourt.court.name;
				this.filterData = {
					...this.filterData,
					courtValue: selectedCourt.court.name,
					isFromCourt: false
				};
			}
		}
	}

	removeAllErrors() {
		this.timeSlotWrapper.classList.remove('is-invalid');
		this.courtWrapper.classList.remove('is-invalid');

		this.startTimeSlotElement.setError('', this.timeSlotErrorElement);
		this.endTimeSlotElement.setError('', this.timeSlotErrorElement);
		this.courtElement.setError('', this.courtErrorElement);
		this.errorAPIDesktop?.classList.add('u-hidden');
		this.errorAPIMobile?.classList.add('u-hidden');
	}

	selectCourtOption(slectedPostion) {
		for (const option of this.courtElement._selectNativeElement) {
			if (option.value === this.filteredData[slectedPostion].court.name) {
				return option;
			}
		}
		return false;
	}

	datePickerValueSet(value) {
		if (this.isDateNotAvailable(new Date(value))) {
			value = '';
		}
		this.date = value;
		this.datePickerComponent.dataValue = value;
		this.avaibilityDatePickerComponent.dataValue = value;
		this.filterData = {
			...this.filterData,
			dateValue: value,
			isFromCourt: true
		};
	}

	startTimeSlotChangeHandler() {
		const selectedSlot = this.startTimeSlotElement._selectNativeElement.value;
		this.filterData = {
			...this.filterData,
			startTimeValue: selectedSlot,
			isFromCourt: true
		};
		this.checkStartEndTimeError();
	}

	endTimeSlotChangeHandler() {
		const selectedSlot = this.endTimeSlotElement._selectNativeElement.value;
		this.filterData = {
			...this.filterData,
			endTimeValue: selectedSlot,
			isFromCourt: true
		};
		this.checkStartEndTimeError();
	}

	checkStartEndTimeError() {
		if (this.filterData.startTimeValue !== '' || this.filterData.endTimeValue !== '') {
			this.removeAllErrors();
			if (this.filterData.startTimeValue === undefined || this.filterData.startTimeValue === '') {
				this.startTimeselectedElement.classList.remove('is-not-image');
			} else {
				this.startTimeselectedElement.classList.add('is-not-image');
			}

			if (this.filterData.endTimeValue === undefined || this.filterData.endTimeValue === '') {
				this.endTimeselectedElement.classList.remove('is-not-image');
			} else {
				this.endTimeselectedElement.classList.add('is-not-image');
			}
		} else {
			this.startTimeselectedElement.classList.remove('is-not-image');
			this.endTimeselectedElement.classList.remove('is-not-image');
		}
	}

	courtChangeHandler() {
		const selectedCourt = this.courtElement._selectNativeElement.value;
		this.filterData = {
			...this.filterData,
			courtValue: selectedCourt,
			isFromCourt: false
		};
		this.storeCourtDetails(this.filterData);
		this.removeAllErrors();
	}

	storeCourtDetails(filterData) {
		const localStorageItems = JSON.parse(localStorage.getItem('venueData'));
		const filterObject = {
			...localStorageItems,
			[this.venueId]: {
				date: filterData.dateValue,
				startTime: filterData.startTimeValue,
				endTime: filterData.endTimeValue,
				court: filterData.courtValue,
				created_at: new Date()
			}
		};
		localStorage.setItem('venueData', JSON.stringify(filterObject));
	}

	async fetchMemberShipAndLinkedVenuesDetails() {
		try {
			const url = `/api/courtdetail/membershipandlinkedvenuesdetails`;
			const response = await Request.get(
				url,
				{
					venueid: this.venueId
				},
				{
					local: IS_LOCAL,
					req: {
						apiMode: ApiMode.LtaError
					}
				}
			);
			const { memberships, linkedVenues, membershipAndBookingInfo, linkedVenueInfo, error } = response;

			if (memberships?.length > 0) {
				const htmlContent = memberships.map((data) => this.renderMembershipOptionsCardData(data)).join('');
				this.showTab(MEMEBERSHIP_IDENTIFIER, this.memebrshipCardList, htmlContent);
				const membershipDescriptionElement = SelectorEngine.findOne('#membership_description');
				if (membershipDescriptionElement) {
					membershipDescriptionElement.textContent = membershipAndBookingInfo || '';
				}
			}
			if (linkedVenues?.length > 0) {
				const htmlContent = this.tableRenderer(linkedVenues);
				this.showTab(LINKEDVENUE_IDENTIFIER, this.linkedVenuesList, htmlContent);
				const linkedVenueDescriptionElement = SelectorEngine.findOne('#venue_description');
				if (linkedVenueDescriptionElement) {
					linkedVenueDescriptionElement.textContent = linkedVenueInfo || '';
				}
			}
			if (!!error?.hasError) {
				throw error;
			}
		} catch (error) {
			if (error.hasError) {
				console.log('Error :', error);
			}
		}
	}
	hideTab(tabIndetifier, tabPanelElement) {
		const tabs = SelectorEngine.find('.lta-tablist__tab', this.tabList);
		for (const tab of tabs) {
			const tabElementIdentifier = Manipulator.getDataAttribute(tab, 'tab-identifier');
			if (tabElementIdentifier === tabIndetifier) {
				tab.classList.add('u-hidden');
			}
		}
		tabPanelElement.innerHTML = '';
	}

	showTab(tabIndetifier, tabPanelElement, content) {
		const tabs = SelectorEngine.find('.lta-tablist__tab', this.tabList);
		for (const tab of tabs) {
			const tabElementIdentifier = Manipulator.getDataAttribute(tab, 'tab-identifier');
			if (tabElementIdentifier === tabIndetifier) {
				tab.classList.remove('u-hidden');
			}
		}
		tabPanelElement.innerHTML = content;
	}

	tableDataRenderer(rowData) {
		const { streetAddress, addressLocality, addressCountry, postalCode, venueName, venueUrl } = rowData;
		const data = [streetAddress, addressLocality, addressCountry, postalCode].filter((index) => !!index).join(', ');
		return `<tr>
					<td><a href="${venueUrl}" rel="" target="_blank">${venueName}</a></td>
					<td>${data}</td>
				</tr>`;
	}

	tableRenderer(rowData) {
		const tableBodyElement = rowData.map((data) => this.tableDataRenderer(data)).join('');
		return ` <div class="lta-richtext u-mb-2">
			<table class="table table-striped">
				<thead>
					<tr>
						<th>Venue</th>
						<th>Address</th>
					</tr>
				</thead>
				<tbody class="">
					${tableBodyElement}
				</tbody>
			</table>
		</div>`;
	}

	renderMembershipOptionsCardData(rowData) {
		return `<div class="p-venue-details__membersip-card u-mt-3 u-flex">
					<div class="u-bg-background-blue u-rounded u-py-2 u-px-3 u-mb-3 u-h-100">
						<div class="u-caption1">${rowData.name}</div>
						<div class="u-body1">${rowData.duration}</div>
						<div class="u-body1">Eligibility: ${rowData.eligibility}</div>
						<div class="u-body1">${rowData.price}</div>
					</div>
					<a class="lta-btn lta-btn--default lta-btn--line lta-btn--block p-venue-details__membersip-card-button-desktop" title="Purchase Family Membership" href="${rowData.url}" target="_blank">Purchase ${rowData.name}</a>
					<a class="lta-btn lta-btn--sm lta-btn--line lta-btn--block p-venue-details__membersip-card-button-mobile" title="Purchase Family Membership" href="${rowData.url}" target="_blank">Purchase ${rowData.name}</a>
				</div>`;
	}

	async fetchSlots(dateValue) {
		startLoadingAnimation(true);
		try {
			const url = `/api/courtdetail/availability`;
			const response = await Request.get(
				url,
				{
					venueid: this.venueId,
					date: dateValue
				},
				{
					local: IS_LOCAL,
					req: {
						apiMode: ApiMode.LtaError
					}
				}
			);
			const { venueDetails, rules, error } = response;

			if (!!error?.hasError) {
				throw error;
			}

			const availableVenueDetails = venueDetails.filter((venue) => venue.availableSlots.length >= rules.minSlots);
			this.rules = rules;
			this.rowData = availableVenueDetails;
			this.slots = this._updateSlotOptions(availableVenueDetails);

			if (this.rules.exceededBookings) {
				this.errorExceedDesktop.classList.remove('u-hidden');
				this.errorExceedMobile.classList.remove('u-hidden');
			} else {
				if (!this.errorExceedDesktop.classList.contains('u-hidden')) {
					this.errorExceedDesktop.classList.add('u-hidden');
				}
				if (!this.errorExceedMobile.classList.contains('u-hidden')) {
					this.errorExceedMobile.classList.add('u-hidden');
				}
			}

			const courtSlotIntervals = availableVenueDetails.map((venueDetail) => {
				const slot = venueDetail.availableSlots?.[0];
				const endTime = dayjs(slot.endTime);
				const startTime = dayjs(slot.startTime);

				const difference = endTime.diff(startTime);

				const courtSlotInterval = difference / ONE_MINUTE_IN_MILLIS;
				return courtSlotInterval;
			});

			// Get the minimum slot interval to render the proper timestamps in CourtAvailability/index.js
			const slotInterval = Math.min(...courtSlotIntervals);
			
			const startTimeDataValues = [...new Set(this.slots.map((slot) => slot.startTime))];
			const endTimeDataValues = [...new Set(this.slots.map((slot) => slot.endTime))];

			const allSlots = [...new Set([...startTimeDataValues, ...endTimeDataValues])];

			allSlots.sort();

			if (this.date && !!availableVenueDetails) {
				this.courtAvailability =
					CourtAvailability.getInstance(this.availabliblityTable) || new CourtAvailability(this.availabliblityTable);
				this.courtAvailability.config = {
					slotInterval: slotInterval,
					selectedDate: this.date,
					slots: allSlots,
					startTime: rules.openingTime,
					endTime: rules.closingTime
				};
				this.courtAvailability.venueDetails = availableVenueDetails;
				this.courtAvailability.onSlotClick((event) => {
					this.slotClickHandler(event);
				});
				this.clearNotAvailableState();
			} else {
				this.showDateNotAvailable();
			}
			this.datePickerWrapper.classList.remove('is-api-error');
			this.errorWrapper.classList.add('u-hidden');
		} catch (error) {
			if (error.hasError) {
				this.datePickerWrapper.classList.add('is-api-error');
				this.errorWrapper.classList.remove('u-hidden');
				this.showDateNotAvailable();
			}
		} finally {
			stopLoadingAnimation();
		}
	}

	showDateNotAvailable() {
		if (this?.courtAvailability) {
			this?.courtAvailability.clearTable();
		}
	}

	clearNotAvailableState() {
		this.courtAvailability.clearNotAvailableState();
	}

	formatAMPM(date) {
		let hours = date.getHours();
		let minutes = date.getMinutes();
		const ampm = hours >= 12 ? 'pm' : 'am';
		hours = hours % 12;
		hours = hours ? hours : 12; // the hour '0' should be '12'
		minutes = minutes < 10 ? '0' + minutes : minutes;
		const stringTime = hours + ':' + minutes + ' ' + ampm;
		return stringTime;
	}

	_updateTimeSlots(slotElement, timeData = {}) {
		slotElement.clearItems();
		const element = SelectorEngine.findOne('select', slotElement._element);
		const options = [
			`<option value="" disabled="" selected="" hidden=""> Please Select</option>`,
			...Object.keys(timeData)
				.sort((object1, object2) => new Date(timeData[object1]) - new Date(timeData[object2]))
				.map((index) => {
					const value = timeData[index];
					return `<option data-ss-id="${value}" value="${value}" ${value === element.value ? 'selected' : ``}>
								${timeConversion(value)}
							</option>`;
				})
		].join('');
		element.innerHTML = options;
		slotElement.generateItems();
	}

	_updateCourts() {
		this.courtElement.clearItems();
		const element = SelectorEngine.findOne('select', this.courtElement._element);
		const options = [
			`<option value="" disabled="" selected="">- Please select -</option>`,
			...Object.keys(this.courtsData).map((index) => {
				const courtData = this.courtsData[index];
				return `<option data-ss-id="${courtData.name}" value="${courtData.name}" ${courtData.name === element.value ? 'selected' : ``}>
							${courtData.name} (${courtData.type} - ${courtData.surface} court)
						</option>`;
			})
		].join('');
		element.innerHTML = options;
		this.courtElement.generateItems();
	}

	filterSlotOption() {
		if (this.datePickerWrapper) {
			this.datePickerWrapper.classList.remove('is-api-error');
			this.errorWrapper.classList.add('u-hidden');
		}
		this.filteredData = this.slots.filter((slot) => {
			if (this.filterData.startTimeValue && this.filterData.startTimeValue !== slot.startTime) {
				return false;
			}

			if (this.filterData.endTimeValue && this.filterData.endTimeValue !== slot.endTime) {
				return false;
			}

			if (this.filterData.courtValue && this.filterData.courtValue !== slot.court.name) {
				return false;
			}
			return true;
		});

		this.startTimeData = [...new Set(this.filteredData.map((slot) => slot.startTime))];
		this.endTimeData = [...new Set(this.filteredData.map((slot) => slot.endTime))];
		this.courtsData = [...new Set(this.filteredData.map((slot) => slot.court))];

		if (this.filterData.isFromCourt) {
			this.prePopulateCourt();
		}

		if (
			this.filterData.startTimeValue &&
			this.filterData.endTimeValue &&
			this.courtElement._selectNativeElement.value &&
			this.courtElement._selectNativeElement.value !== 'Any available court'
		) {
			let groupBookingPrefix = this.filteredData.some((court) =>  court.slots.some(slot => slot.isGroupBookingEnabled));
            const totalPrice = this.courtsPrice(this.filteredData[0].slots);
			this.desktopPriceLabel.innerHTML = `${groupBookingPrefix ? this.groupBookingPriceText : this.priceText}`;
			this.mobilePriceLabel.innerHTML = `${groupBookingPrefix ? this.groupBookingPriceText : this.priceText}`;
            this.bookingAmount.innerHTML = `${groupBookingPrefix ? `${this.pricePrefixText}` : ''} £${totalPrice}`;
            this.bookingAmountMobile.innerHTML = `${groupBookingPrefix ? `${this.pricePrefixText}` : ''} £${totalPrice}`;

			this.bookingPrice.classList.remove('u-hidden');
			this.bookingPriceMobile.classList.remove('u-hidden');
		} else {
			this.bookingPrice.classList.add('u-hidden');
			this.bookingPriceMobile.classList.add('u-hidden');
		}

		this._updateTimeSlots(this.startTimeSlotElement, this.startTimeData);
		this._updateTimeSlots(this.endTimeSlotElement, this.endTimeData);
		this._updateCourts();

		if (this.fromStoreValues) {
			this.fromStoreValues = false;
			this.showSelectedValue(this.startTimeSlotElement, this.filterData.startTimeValue);
			this.showSelectedValue(this.endTimeSlotElement, this.filterData.endTimeValue);
			this.showSelectedValue(this.courtElement, this.filterData.courtValue);
		}
	}

	showSelectedValue(element, value) {
		let selectedOption;
		for (const option of element._selectNativeElement.options) {
			if (option.value === value) {
				selectedOption = option;
			}
		}
		if(selectedOption) {
			element.selectedOptions = [selectedOption];
			this.checkStartEndTimeError();
		}
	}

	courtsPrice(slots) {
		return Number.parseFloat(slots.reduce((previousValue, nextValue) => previousValue + nextValue.price, 0)).toFixed(2);
	}

	_updateSlotOptions(courts = []) {
		const slotDatabase = [];
		for (const court of courts) {
			if (!court.availableSlots[0]) {
				continue;
			}
			const slotDuration = getSlotDuration(court.availableSlots[0].startTime, court.availableSlots[0].endTime);
			const minSlots = this.rules?.minSlots || 1;
			const maxSlots = this.rules?.maxSlots || 1;

			for (const [index, slot] of court.availableSlots.entries()) {
				const includingSlots = [];
				const minSlotObject = {
					startTime: slot.startTime,
					slots: [slot],
					court: court
				};

				const nextSlots = court.availableSlots?.slice(index, minSlots + index);
				if (nextSlots.length < minSlots) {
					// If court has less nex slots than minimum required.
					continue;
				}

				const isMinSlotNotAvailable = court.availableSlots
					.slice(index, minSlots + index)
					.some((actualNextSlot, index_) => {
						const nextSlotTime = getFollowingSlotStartTime(slot.startTime, slotDuration, index_);
						const hasGivenSlot = new Date(actualNextSlot.startTime).getTime() === nextSlotTime.getTime();
						minSlotObject.endTime = actualNextSlot.endTime;
						includingSlots.push(actualNextSlot);
						return !hasGivenSlot;
					});

				if (!isMinSlotNotAvailable) {
					slotDatabase.push({
						...minSlotObject,
						slots: [...includingSlots]
					});

					// Logic - Check for slots available upto max slot and make entries.
					for (let counter = minSlots; counter < maxSlots; counter++) {
						const nextSlotTime = getFollowingSlotStartTime(slot.startTime, slotDuration, counter);
						const actualNextSlot = court.availableSlots[counter + index];
						if (
							court.availableSlots[counter + index] &&
							nextSlotTime.getTime() === new Date(actualNextSlot?.startTime).getTime()
						) {
							minSlotObject.endTime = actualNextSlot.endTime;
							includingSlots.push(actualNextSlot);
							slotDatabase.push({
								...minSlotObject,
								slots: [...includingSlots]
							});
						} else {
							break;
						}
					}
				}
			}
		}
		return slotDatabase || [];
	}

	openBookingPanel() {
		this._wrapper.classList.add('is-booking-open');
		window.scrollTo(0, 0);
	}

	closeBookingPanel() {
		this._wrapper.classList.remove('is-booking-open');
	}

	pushDataLayer(event, action) {
		if(!dataLayer) {
			return;
		}
		const defaultFavouriteData = {
			event: event,
			venue_id: this.venueId,
			venue_name: this.venueName,
			screen: 'venue_details',
		};
		switch(event) {
			case 'favourite_click':
			case 'favourite_login_prompt_shown':
			case 'favourite_limit_reached_prompt_shown':
				dataLayer.push({
					...defaultFavouriteData,
				});
				break;
								
			case 'favourite_added':
			case 'favourite_removed':
				dataLayer.push({
					...defaultFavouriteData,
					favourite_status: action,
				});
				break;
		}
	}
}

const globalVenueDetails = new VenueDetails();
export default globalVenueDetails;
